import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useIntl } from 'react-intl';

// material-ui
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Chip from '@mui/material/Chip';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Collapse from '@mui/material/Collapse';
import Avatar from '@mui/material/Avatar';
import Menu from '@mui/material/Menu';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Divider from '@mui/material/Divider';

// icons
import MessageOutlined from '@ant-design/icons/MessageOutlined';
import CloseOutlined from '@ant-design/icons/CloseOutlined';
import SendOutlined from '@ant-design/icons/SendOutlined';
import PlusOutlined from '@ant-design/icons/PlusOutlined';
import CommentOutlined from '@ant-design/icons/CommentOutlined';
import ExpandMoreOutlined from '@ant-design/icons/DownOutlined';
import ExpandLessOutlined from '@ant-design/icons/UpOutlined';
import EditOutlined from '@ant-design/icons/EditOutlined';
import FolderOutlined from '@ant-design/icons/FolderOutlined';

// project imports
import useAuth from 'hooks/useAuth';
import { useFeedback } from 'contexts/FeedbackContext';
import {
  createFeedback,
  getFeedbackList,
  addComment,
  updateFeedback,
  STATUS_OPTIONS,
  PRIORITY_LEVELS
} from 'services/feedbackService';

// ==============================|| FEEDBACK WIDGET ||============================== //

export default function FeedbackWidget() {
  const theme = useTheme();
  const location = useLocation();
  const intl = useIntl();
  const { user, isLoggedIn } = useAuth();
  const { isOpen: open, closeFeedback } = useFeedback();
  const [tab, setTab] = useState(0); // 0 = view feedback, 1 = add feedback
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Feedback list state
  const [feedbackList, setFeedbackList] = useState([]);
  const [feedbackCount, setFeedbackCount] = useState(0);
  const [expandedId, setExpandedId] = useState(null);
  const [newComment, setNewComment] = useState('');
  const [statusFilter, setStatusFilter] = useState('open'); // 'all', 'open', 'in_progress', 'resolved'
  const [pageFilter, setPageFilter] = useState('current'); // 'current' or 'all'
  const [statusMenuAnchor, setStatusMenuAnchor] = useState(null);
  const [editingStatusId, setEditingStatusId] = useState(null);
  const [collapsedGroups, setCollapsedGroups] = useState({}); // Track collapsed page groups

  // Edit feedback state
  const [editingFeedback, setEditingFeedback] = useState(null);
  const [editFormData, setEditFormData] = useState({
    description: '',
    priority: 'medium'
  });

  // New feedback form state
  const [formData, setFormData] = useState({
    description: '',
    priority: 'medium'
  });

  // Check if feedback is enabled via environment variable
  const isFeedbackEnabled = import.meta.env.VITE_APP_ENABLE_FEEDBACK === 'true';

  // Fetch feedback for current page or all pages
  const fetchFeedback = async () => {
    try {
      setLoading(true);
      const params = {
        pageSize: 50
      };
      // Only filter by page if pageFilter is 'current'
      if (pageFilter === 'current') {
        params.pagePath = location.pathname;
      }
      // Apply status filter if not 'all'
      if (statusFilter !== 'all') {
        params.status = statusFilter;
      }
      const response = await getFeedbackList(params);
      setFeedbackList(response.items || []);
      setFeedbackCount(response.total || 0);
    } catch (err) {
      console.error('Failed to fetch feedback:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch on open, page change, or filter change
  useEffect(() => {
    if (open) {
      fetchFeedback();
    }
  }, [open, location.pathname, statusFilter, pageFilter]);

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Submit new feedback
  const handleSubmitFeedback = async () => {
    if (!formData.description.trim()) {
      setError(intl.formatMessage({ id: 'feedback.pleaseEnter' }));
      return;
    }

    try {
      setLoading(true);
      setError('');

      // Generate title from first 50 chars of description
      const autoTitle = formData.description.trim().substring(0, 50) + (formData.description.length > 50 ? '...' : '');

      await createFeedback(
        {
          title: autoTitle,
          description: formData.description,
          feedback_type: 'feedback',
          priority: formData.priority,
          page_path: location.pathname,
          page_title: document.title,
          browser_info: {
            userAgent: navigator.userAgent,
            language: navigator.language,
            screenSize: `${window.innerWidth}x${window.innerHeight}`
          }
        },
        user.id
      );

      setSuccess(intl.formatMessage({ id: 'feedback.submitSuccess' }));
      setFormData({ description: '', priority: 'medium' });
      setTab(0);
      fetchFeedback();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : detail || 'Failed to submit feedback';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Add comment to feedback
  const handleAddComment = async (feedbackId) => {
    if (!newComment.trim()) return;

    try {
      setLoading(true);
      await addComment(feedbackId, { comment: newComment }, user.id);
      setNewComment('');
      fetchFeedback();
    } catch (err) {
      const detail = err.response?.data?.detail;
      const message = Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : detail || 'Failed to add comment';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  // Update feedback status
  const handleStatusChange = async (feedbackId, newStatus) => {
    // Close the menu immediately before any state changes
    setStatusMenuAnchor(null);
    setEditingStatusId(null);

    try {
      setLoading(true);
      await updateFeedback(feedbackId, { status: newStatus }, user.id);
      fetchFeedback();
      setSuccess(`Status updated to ${STATUS_OPTIONS[newStatus]?.label || newStatus}`);
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) {
      { const detail = err.response?.data?.detail; setError(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : detail || 'Failed to update feedback'); }
    } finally {
      setLoading(false);
    }
  };

  // Open status change menu
  const handleOpenStatusMenu = (event, feedbackId) => {
    setStatusMenuAnchor(event.currentTarget);
    setEditingStatusId(feedbackId);
  };

  // Close status change menu
  const handleCloseStatusMenu = () => {
    setStatusMenuAnchor(null);
    setEditingStatusId(null);
  };

  // Start editing feedback
  const handleStartEdit = (feedback) => {
    setEditingFeedback(feedback);
    setEditFormData({
      description: feedback.description,
      priority: feedback.priority || 'medium'
    });
  };

  // Cancel editing
  const handleCancelEdit = () => {
    setEditingFeedback(null);
    setEditFormData({ description: '', priority: 'medium' });
  };

  // Save edited feedback
  const handleSaveEdit = async () => {
    if (!editFormData.description.trim()) {
      setError(intl.formatMessage({ id: 'feedback.pleaseEnter' }));
      return;
    }

    try {
      setLoading(true);
      setError('');

      const autoTitle = editFormData.description.trim().substring(0, 50) + (editFormData.description.length > 50 ? '...' : '');

      await updateFeedback(
        editingFeedback.id,
        {
          title: autoTitle,
          description: editFormData.description,
          priority: editFormData.priority
        },
        user.id
      );

      setSuccess(intl.formatMessage({ id: 'feedback.updateSuccess' }));
      setEditingFeedback(null);
      setEditFormData({ description: '', priority: 'medium' });
      fetchFeedback();

      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      { const detail = err.response?.data?.detail; setError(Array.isArray(detail) ? detail.map(d => d.msg).join(', ') : detail || 'Failed to update feedback'); }
    } finally {
      setLoading(false);
    }
  };

  // Handle edit form input change
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Group feedback by page path and sort by date (for "All Pages" view)
  const getGroupedFeedback = () => {
    if (pageFilter !== 'all' || feedbackList.length === 0) {
      return null;
    }

    // Group by page_path
    const grouped = feedbackList.reduce((acc, feedback) => {
      const path = feedback.page_path || '/unknown';
      if (!acc[path]) {
        acc[path] = [];
      }
      acc[path].push(feedback);
      return acc;
    }, {});

    // Sort feedback within each group by date (newest first)
    Object.keys(grouped).forEach((path) => {
      grouped[path].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
    });

    // Sort groups by the most recent feedback date in each group
    const sortedPaths = Object.keys(grouped).sort((a, b) => {
      const latestA = new Date(grouped[a][0]?.created_at || 0);
      const latestB = new Date(grouped[b][0]?.created_at || 0);
      return latestB - latestA;
    });

    return { grouped, sortedPaths };
  };

  // Toggle group collapse
  const toggleGroupCollapse = (path) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [path]: !prev[path]
    }));
  };

  // Render feedback card
  const renderFeedbackCard = (feedback) => {
    const isExpanded = expandedId === feedback.id;
    const statusInfo = STATUS_OPTIONS[feedback.status] || STATUS_OPTIONS.open;
    const isOpen = feedback.status === 'open';
    const isOwner = feedback.user_id === user.id || feedback.user?.id === user.id;

    return (
      <Card
        key={feedback.id}
        variant="outlined"
        sx={{
          mb: 1.5,
          overflow: 'hidden',
          ...(isOpen && {
            borderColor: 'info.main',
            borderWidth: 1.5,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(33, 150, 243, 0.08)' : 'rgba(33, 150, 243, 0.04)'
          })
        }}
      >
        <CardContent sx={{ pb: 1 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1, mr: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1} alignItems="flex-start">
                <MessageOutlined style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography
                  variant="subtitle2"
                  sx={{
                    wordBreak: 'break-word',
                    overflowWrap: 'break-word',
                    hyphens: 'auto'
                  }}
                >
                  {feedback.title}
                </Typography>
              </Stack>
            </Box>
            <Chip label={statusInfo.label} size="small" color={statusInfo.color} sx={{ flexShrink: 0 }} />
          </Stack>

          <Typography variant="caption" color="textSecondary" sx={{ mt: 0.5, display: 'block' }}>
            by {feedback.user?.full_name || feedback.user?.username} •{' '}
            {new Date(feedback.created_at).toLocaleDateString()}
            {feedback.page_path && (
              <>
                {' '}• <strong>{feedback.page_path}</strong>
              </>
            )}
          </Typography>

          <Collapse in={isExpanded}>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                overflowWrap: 'break-word'
              }}
            >
              {feedback.description}
            </Typography>

            {/* Comments */}
            {feedback.comments?.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="textSecondary" fontWeight={600}>
                  {intl.formatMessage({ id: 'feedback.commentsCount' }, { count: feedback.comments.length })}
                </Typography>
                {feedback.comments.map((comment) => (
                  <Box
                    key={comment.id}
                    sx={{
                      mt: 1,
                      p: 1.5,
                      bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'grey.100',
                      borderRadius: 1,
                      border: '1px solid',
                      borderColor: theme.palette.mode === 'dark' ? 'grey.700' : 'grey.200'
                    }}
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                        {comment.user?.username?.charAt(0).toUpperCase()}
                      </Avatar>
                      <Typography variant="caption" fontWeight={600} color="text.primary">
                        {comment.user?.full_name || comment.user?.username}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {new Date(comment.created_at).toLocaleDateString()}
                      </Typography>
                      {comment.is_resolution && (
                        <Chip label={intl.formatMessage({ id: 'feedback.resolution' })} size="small" color="success" sx={{ height: 18 }} />
                      )}
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
                      {comment.comment}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}

            {/* Add comment */}
            <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
              <TextField
                size="small"
                fullWidth
                placeholder={intl.formatMessage({ id: 'feedback.addComment' })}
                value={expandedId === feedback.id ? newComment : ''}
                onChange={(e) => setNewComment(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleAddComment(feedback.id)}
              />
              <IconButton
                color="primary"
                onClick={() => handleAddComment(feedback.id)}
                disabled={!newComment.trim()}
              >
                <SendOutlined />
              </IconButton>
            </Stack>
          </Collapse>
        </CardContent>

        <CardActions sx={{ pt: 0, flexWrap: 'wrap', gap: 0.5 }}>
          <Button
            size="small"
            onClick={() => setExpandedId(isExpanded ? null : feedback.id)}
            endIcon={isExpanded ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}
          >
            {isExpanded ? intl.formatMessage({ id: 'feedback.collapse' }) : intl.formatMessage({ id: 'feedback.expand' })}
          </Button>
          {isOwner && (
            <Button
              size="small"
              color="primary"
              startIcon={<EditOutlined />}
              onClick={() => handleStartEdit(feedback)}
            >
              {intl.formatMessage({ id: 'feedback.edit' })}
            </Button>
          )}
          <Button
            size="small"
            color="secondary"
            onClick={(e) => handleOpenStatusMenu(e, feedback.id)}
          >
            {intl.formatMessage({ id: 'feedback.status' })}
          </Button>
          <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
            <CommentOutlined /> {feedback.comment_count || 0}
          </Typography>
        </CardActions>
      </Card>
    );
  };

  return (
    <>
      {/* Drawer */}
      <Drawer
        anchor="right"
        open={open}
        onClose={closeFeedback}
        PaperProps={{
          sx: { width: { xs: '100%', sm: 420 } }
        }}
      >
        {/* Header */}
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}
        >
          <Typography variant="h5">{intl.formatMessage({ id: 'feedback.pageFeedback' })}</Typography>
          <IconButton onClick={closeFeedback}>
            <CloseOutlined />
          </IconButton>
        </Stack>

        {/* Tabs */}
        <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab label={intl.formatMessage({ id: 'feedback.feedbackCount' }, { count: feedbackCount })} />
          <Tab label={intl.formatMessage({ id: 'feedback.addNew' })} icon={<PlusOutlined />} iconPosition="start" />
        </Tabs>

        {/* Content */}
        <Box sx={{ p: 2, height: 'calc(100vh - 140px)', overflow: 'auto' }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
              {error}
            </Alert>
          )}
          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
              {success}
            </Alert>
          )}

          {/* View Feedback Tab */}
          {tab === 0 && (
            <>
              {/* Page Filter */}
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="textSecondary">
                  {intl.formatMessage({ id: 'feedback.showFor' })}
                </Typography>
                <Chip
                  label={intl.formatMessage({ id: 'feedback.thisPage' })}
                  size="small"
                  variant={pageFilter === 'current' ? 'filled' : 'outlined'}
                  color={pageFilter === 'current' ? 'primary' : 'default'}
                  onClick={() => setPageFilter('current')}
                />
                <Chip
                  label={intl.formatMessage({ id: 'feedback.allPages' })}
                  size="small"
                  variant={pageFilter === 'all' ? 'filled' : 'outlined'}
                  color={pageFilter === 'all' ? 'primary' : 'default'}
                  onClick={() => setPageFilter('all')}
                />
              </Stack>

              {pageFilter === 'current' && (
                <Typography variant="caption" color="textSecondary" sx={{ mb: 1, display: 'block' }}>
                  {intl.formatMessage({ id: 'feedback.currentPage' })}<strong>{location.pathname}</strong>
                </Typography>
              )}

              {/* Status Filter */}
              <Stack direction="row" spacing={0.5} sx={{ mb: 2, flexWrap: 'wrap', gap: 0.5 }}>
                <Chip
                  label={intl.formatMessage({ id: 'feedback.statusOpen' })}
                  size="small"
                  variant={statusFilter === 'open' ? 'filled' : 'outlined'}
                  color={statusFilter === 'open' ? 'info' : 'default'}
                  onClick={() => setStatusFilter('open')}
                />
                <Chip
                  label={intl.formatMessage({ id: 'feedback.statusAll' })}
                  size="small"
                  variant={statusFilter === 'all' ? 'filled' : 'outlined'}
                  color={statusFilter === 'all' ? 'primary' : 'default'}
                  onClick={() => setStatusFilter('all')}
                />
                <Chip
                  label={intl.formatMessage({ id: 'feedback.statusInProgress' })}
                  size="small"
                  variant={statusFilter === 'in_progress' ? 'filled' : 'outlined'}
                  color={statusFilter === 'in_progress' ? 'warning' : 'default'}
                  onClick={() => setStatusFilter('in_progress')}
                />
                <Chip
                  label={intl.formatMessage({ id: 'feedback.statusResolved' })}
                  size="small"
                  variant={statusFilter === 'resolved' ? 'filled' : 'outlined'}
                  color={statusFilter === 'resolved' ? 'success' : 'default'}
                  onClick={() => setStatusFilter('resolved')}
                />
                <Chip
                  label={intl.formatMessage({ id: 'feedback.statusClosed' })}
                  size="small"
                  variant={statusFilter === 'closed' ? 'filled' : 'outlined'}
                  color={statusFilter === 'closed' ? 'default' : 'default'}
                  onClick={() => setStatusFilter('closed')}
                />
              </Stack>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : feedbackList.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <MessageOutlined style={{ fontSize: 48, color: theme.palette.text.secondary }} />
                  <Typography color="textSecondary" sx={{ mt: 1 }}>
                    {statusFilter === 'all'
                      ? pageFilter === 'all'
                        ? intl.formatMessage({ id: 'feedback.noFeedbackYet' })
                        : intl.formatMessage({ id: 'feedback.noFeedbackThisPage' })
                      : intl.formatMessage({ id: 'feedback.noFilteredFeedback' }, { status: statusFilter.replace('_', ' ') })}
                  </Typography>
                  <Button variant="outlined" sx={{ mt: 2 }} onClick={() => setTab(1)}>
                    {intl.formatMessage({ id: 'feedback.addFeedback' })}
                  </Button>
                </Box>
              ) : pageFilter === 'all' ? (
                // Grouped view for "All Pages"
                (() => {
                  const groupedData = getGroupedFeedback();
                  if (!groupedData) return feedbackList.map(renderFeedbackCard);

                  const { grouped, sortedPaths } = groupedData;
                  return sortedPaths.map((path, index) => {
                    const isCollapsed = collapsedGroups[path];
                    return (
                      <Box key={path}>
                        {/* Page Route Header - Compact & Clickable */}
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={0.5}
                          onClick={() => toggleGroupCollapse(path)}
                          sx={{
                            py: 0.75,
                            px: 1,
                            mt: index > 0 ? 1.5 : 0,
                            mb: 0.5,
                            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : 'grey.50',
                            borderRadius: 0.5,
                            borderLeft: 2,
                            borderColor: 'primary.main',
                            cursor: 'pointer',
                            '&:hover': {
                              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : 'grey.100'
                            }
                          }}
                        >
                          <IconButton size="small" sx={{ p: 0.25 }}>
                            {isCollapsed ? (
                              <ExpandMoreOutlined style={{ fontSize: 14 }} />
                            ) : (
                              <ExpandLessOutlined style={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                          <FolderOutlined style={{ fontSize: 14, color: theme.palette.primary.main }} />
                          <Typography
                            variant="caption"
                            fontWeight={600}
                            sx={{
                              flex: 1,
                              minWidth: 0,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              color: 'primary.main'
                            }}
                          >
                            {path}
                          </Typography>
                          <Chip
                            label={grouped[path].length}
                            size="small"
                            sx={{
                              height: 18,
                              fontSize: '0.65rem',
                              '& .MuiChip-label': { px: 0.75 }
                            }}
                          />
                        </Stack>

                        {/* Collapsible Feedback cards for this page */}
                        <Collapse in={!isCollapsed}>
                          {grouped[path].map(renderFeedbackCard)}
                        </Collapse>

                        {/* Divider between groups */}
                        {index < sortedPaths.length - 1 && (
                          <Divider sx={{ mt: 1.5 }} />
                        )}
                      </Box>
                    );
                  });
                })()
              ) : (
                feedbackList.map(renderFeedbackCard)
              )}
            </>
          )}

          {/* Add Feedback Tab */}
          {tab === 1 && (
            <Stack spacing={2}>
              <Typography variant="caption" color="textSecondary">
                {intl.formatMessage({ id: 'feedback.addingFeedbackFor' })} <strong>{location.pathname}</strong>
              </Typography>

              <TextField
                name="description"
                label={intl.formatMessage({ id: 'feedback.yourFeedback' })}
                fullWidth
                multiline
                minRows={3}
                maxRows={15}
                value={formData.description}
                onChange={handleInputChange}
                placeholder={intl.formatMessage({ id: 'feedback.placeholder' })}
              />

              <TextField
                name="priority"
                label={intl.formatMessage({ id: 'feedback.priority' })}
                select
                fullWidth
                value={formData.priority}
                onChange={handleInputChange}
              >
                {Object.entries(PRIORITY_LEVELS).map(([key, { label }]) => (
                  <MenuItem key={key} value={key}>
                    {label}
                  </MenuItem>
                ))}
              </TextField>

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handleSubmitFeedback}
                disabled={loading || !formData.description.trim()}
                startIcon={loading ? <CircularProgress size={20} /> : <SendOutlined />}
              >
                {intl.formatMessage({ id: 'feedback.submitFeedback' })}
              </Button>
            </Stack>
          )}
        </Box>

        {/* Status Change Menu - inside Drawer for proper positioning */}
        <Menu
          anchorEl={statusMenuAnchor}
          open={Boolean(statusMenuAnchor)}
          onClose={handleCloseStatusMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          keepMounted={false}
          slotProps={{
            paper: {
              sx: { minWidth: 180 }
            }
          }}
        >
          <MenuItem
            onClick={() => handleStatusChange(editingStatusId, 'open')}
            sx={{ color: theme.palette.info.main }}
          >
            <Chip label={intl.formatMessage({ id: 'feedback.statusOpen' })} size="small" color="info" sx={{ mr: 1 }} />
            {intl.formatMessage({ id: 'feedback.markOpen' })}
          </MenuItem>
          <MenuItem
            onClick={() => handleStatusChange(editingStatusId, 'in_progress')}
            sx={{ color: theme.palette.warning.main }}
          >
            <Chip label={intl.formatMessage({ id: 'feedback.statusInProgress' })} size="small" color="warning" sx={{ mr: 1 }} />
            {intl.formatMessage({ id: 'feedback.markInProgress' })}
          </MenuItem>
          <MenuItem
            onClick={() => handleStatusChange(editingStatusId, 'resolved')}
            sx={{ color: theme.palette.success.main }}
          >
            <Chip label={intl.formatMessage({ id: 'feedback.statusResolved' })} size="small" color="success" sx={{ mr: 1 }} />
            {intl.formatMessage({ id: 'feedback.markResolved' })}
          </MenuItem>
          <MenuItem
            onClick={() => handleStatusChange(editingStatusId, 'closed')}
          >
            <Chip label={intl.formatMessage({ id: 'feedback.statusClosed' })} size="small" sx={{ mr: 1 }} />
            {intl.formatMessage({ id: 'feedback.markClosed' })}
          </MenuItem>
        </Menu>
      </Drawer>

      {/* Edit Feedback Dialog */}
      <Dialog
        open={Boolean(editingFeedback)}
        onClose={handleCancelEdit}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{intl.formatMessage({ id: 'feedback.editFeedback' })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              {intl.formatMessage({ id: 'feedback.page' })} <strong>{editingFeedback?.page_path}</strong>
            </Typography>

            <TextField
              name="description"
              label={intl.formatMessage({ id: 'feedback.yourFeedback' })}
              fullWidth
              multiline
              minRows={3}
              maxRows={15}
              value={editFormData.description}
              onChange={handleEditInputChange}
              placeholder={intl.formatMessage({ id: 'feedback.placeholder' })}
            />

            <TextField
              name="priority"
              label={intl.formatMessage({ id: 'feedback.priority' })}
              select
              fullWidth
              value={editFormData.priority}
              onChange={handleEditInputChange}
            >
              {Object.entries(PRIORITY_LEVELS).map(([key, { label }]) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit} color="inherit">
            {intl.formatMessage({ id: 'common.cancel' })}
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={loading || !editFormData.description.trim()}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {intl.formatMessage({ id: 'common.save' })}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
